import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { navStarted, navFinished } from '../api/loading';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/',
    component: () => import('../layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('../views/DashboardView.vue'),
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('../views/OrdersView.vue'),
      },
      {
        path: 'production',
        name: 'production',
        component: () => import('../views/ProductionView.vue'),
      },
      {
        path: 'orders/new',
        name: 'order-new',
        component: () => import('../views/OrderEditView.vue'),
      },
      {
        path: 'orders/:id',
        name: 'order-detail',
        component: () => import('../views/OrderEditView.vue'),
        props: true,
      },
      {
        path: 'clinics',
        name: 'clinics',
        component: () => import('../views/ClinicsView.vue'),
      },
      {
        path: 'doctors',
        name: 'doctors',
        component: () => import('../views/DoctorsView.vue'),
      },
      {
        path: 'price-books',
        name: 'price-books',
        component: () => import('../views/PriceBooksView.vue'),
      },
      {
        path: 'work-codes',
        name: 'work-codes',
        component: () => import('../views/WorkCodesView.vue'),
      },
      {
        path: 'catalogs',
        name: 'catalogs',
        component: () => import('../views/CatalogsView.vue'),
      },
      {
        path: 'import',
        name: 'import',
        component: () => import('../views/ImportView.vue'),
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('../views/ReportsView.vue'),
      },
      {
        path: 'invoices',
        name: 'invoices',
        component: () => import('../views/InvoicesView.vue'),
      },
      {
        path: 'shipments',
        name: 'shipments',
        component: () => import('../views/ShipmentsView.vue'),
      },
      {
        path: 'claims',
        name: 'claims',
        component: () => import('../views/ClaimsView.vue'),
      },
      {
        path: 'load',
        name: 'load',
        component: () => import('../views/LoadView.vue'),
      },
      {
        path: 'agent',
        name: 'agent',
        component: () => import('../views/AgentView.vue'),
      },
      {
        path: 'workflows',
        name: 'workflows',
        component: () => import('../views/WorkflowsView.vue'),
      },
      {
        path: 'technicians',
        name: 'technicians',
        component: () => import('../views/TechniciansView.vue'),
      },
      {
        path: 'scan',
        name: 'scan',
        component: () => import('../views/ScanView.vue'),
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('../views/UsersView.vue'),
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('../views/RolesView.vue'),
      },
    ],
  },
  // Klinikos portalas — atskiras išdėstymas, kliniką mato tik savo užsakymus
  {
    path: '/portal',
    component: () => import('../layouts/PortalLayout.vue'),
    meta: { requiresAuth: true, portal: true },
    children: [
      {
        path: '',
        name: 'portal-orders',
        component: () => import('../views/portal/PortalOrdersView.vue'),
      },
      {
        path: 'new',
        name: 'portal-new',
        component: () => import('../views/portal/PortalNewOrderView.vue'),
      },
      {
        path: 'orders/:id',
        name: 'portal-order',
        component: () => import('../views/portal/PortalOrderView.vue'),
      },
    ],
  },
  {
    path: '/shipping-label',
    name: 'shipping-label',
    component: () => import('../views/ShippingLabelView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/waybill',
    name: 'waybill',
    component: () => import('../views/WaybillView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/picking-list',
    name: 'picking-list',
    component: () => import('../views/PickingListView.vue'),
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Auth guard + krovimo indikatorius
router.beforeEach(async (to) => {
  navStarted();
  const auth = useAuthStore();
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  // Pirmas maršrutas paleidžiamas dar negavus /auth/me — be rolės nežinotume, kur vesti
  if (auth.isAuthenticated && !auth.user) await auth.fetchMe();
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const portalUser = auth.user?.role === 'DOCTOR_PORTAL';
  if (to.meta.requiresGuest && auth.isAuthenticated) {
    return { name: portalUser ? 'portal-orders' : 'dashboard' };
  }
  // Klinikos paskyra į personalo langus nepatenka, personalas — į klinikos portalą
  const portalRoute = to.matched.some((r) => r.meta.portal);
  if (auth.isAuthenticated && portalUser && !portalRoute) return { name: 'portal-orders' };
  if (auth.isAuthenticated && auth.user && !portalUser && portalRoute) return { name: 'dashboard' };
});

router.afterEach(() => {
  navFinished();
});

router.onError(() => {
  navFinished();
});

export default router;
